import './Form.scss'
import { useForm } from 'react-hook-form'
import { Field, Input, Textarea, Select } from '@fluentui/react-components'
// import { useDotnetClient } from '@/contexts/DotnetClientContext'
// import {
//   MessagesApplicationsApplication,
//   MessagesApplicationsApplicationAttributes,
//   MessagesApplicationsPictogram,
//   MessagesApplicationsUser,
// } from '@/dotnet-client'
import { Button } from '../Button'
import { toast } from 'react-toastify'
import { ENDPOINTS } from '@/util'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { Loading } from '../Loading'
import { ErrorComponent } from '../ErrorComponent'
import { useQueryClient } from '@tanstack/react-query'

type MessagesApplicationsApplication = RestSchemas['Messages.Applications.Application']
type MessagesApplicationsApplicationAttributes = RestSchemas['Messages.Applications.ApplicationAttributes']
type MessagesApplicationsPictogram = RestSchemas['Messages.Applications.Pictogram']
type MessagesApplicationsUser = RestSchemas['Messages.Applications.User']

export type ApplicationAttribsForm = {
  problemStatement: string
  keyQuestions: string | null
  solutionDescription: string
  differentiatedBenefit1: string | null
  differentiatedBenefit1Pictogram: string | null
  differentiatedBenefit2: string | null
  differentiatedBenefit2Pictogram: string | null
  differentiatedBenefit3: string | null
  differentiatedBenefit3Pictogram: string | null
  imageKey: string | null

  keyContactId1: number | null
  keyContactId2: number | null
  keyContactId3: number | null
}

type ApplicationAttribsFormProps = {
  onSubmit: () => void
  selectedApp: MessagesApplicationsApplication
  applicationAttribs: MessagesApplicationsApplicationAttributes
  pictograms: MessagesApplicationsPictogram[]
  users: MessagesApplicationsUser[]
}

export const EditApplicationAttribsForm = (
  props: Omit<ApplicationAttribsFormProps, 'applicationAttribs' | 'pictograms' | 'users'>
) => {
  // const { dmpClient } = useDotnetClient()
  const {
    // getApplicationsApiGetApplications,
    getApplicationsApiGetApplicationAttributes,
    getApplicationsApiGetPictograms,
    getApplicationsApiGetUsers,
  } = createRestClient()

  const {
    data: applicationAttribs,
    isPending: applicationAttribsLoading,
    isError: applicationAttribsError,
  } = getApplicationsApiGetApplicationAttributes.useQuery({
    query: {
      applicationId: props.selectedApp.applicationId,
    },
  })
  const {
    data: pictograms,
    isPending: pictogramsLoading,
    isError: pictogramsError,
  } = getApplicationsApiGetPictograms.useQuery()
  const { data: users, isPending: usersLoading, isError: usersError } = getApplicationsApiGetUsers.useQuery()

  if (applicationAttribsLoading || pictogramsLoading || usersLoading) return <Loading />
  if (applicationAttribsError || pictogramsError || usersError) return <ErrorComponent />

  return <Form {...props} applicationAttribs={applicationAttribs} pictograms={pictograms} users={users} />
}

function Form(props: ApplicationAttribsFormProps) {
  const queryClient = useQueryClient()
  const {
    register,
    formState: { errors },
    // setError,
    watch,
    reset,
    handleSubmit,
    // control
  } = useForm<ApplicationAttribsForm>({
    defaultValues: props.applicationAttribs,
  })

  const watchPict1 = watch('differentiatedBenefit1Pictogram')
  const watchPict2 = watch('differentiatedBenefit2Pictogram')
  const watchPict3 = watch('differentiatedBenefit3Pictogram')
  const watchImageKey = watch('imageKey')

  const { postApplicationsApiUpdateApplicationAttributes, getApplicationsApiGetApplicationAttributes } =
    createRestClient()

  const {
    mutate: updateApplicationAttributes,
    // isPending: updateApplicationAttributesLoading,
    // isError: updateApplicationAttributesError,
  } = postApplicationsApiUpdateApplicationAttributes.useMutation()

  const onSubmit = (data: ApplicationAttribsForm) => {
    const updatedApp: MessagesApplicationsApplicationAttributes = {
      applicationId: props.selectedApp!.applicationId!,
      ...data,
    }

    updateApplicationAttributes(
      { body: updatedApp },
      {
        onSuccess() {
          toast.success('App attributes successfully updated')
          getApplicationsApiGetApplicationAttributes.invalidateQueries(
            { parameters: { query: { applicationId: props.selectedApp.applicationId } } },
            queryClient
          )
          props.onSubmit()
          reset()
        },
        onError() {
          toast.error(`Error updating app attribs`)
        },
      }
    )
  }

  return (
    <div className="form-wrapper">
      <div>
        <b>{props.selectedApp!.appName}</b>

        <Field
          label="Image"
          validationState={errors.imageKey ? 'error' : 'none'}
          validationMessage={errors.imageKey?.message}
        >
          <Select
            //placeholder="select a picture"
            {...register('imageKey', {
              maxLength: 256,
            })}
          >
            <option key={'none'} value={undefined}>
              None
            </option>
            {props.pictograms.map((p) => {
              return (
                <option key={p.imageKey} value={p.imageKey}>
                  {p.imageKey}
                </option>
              )
            })}
          </Select>
          {watchImageKey !== null && (
            <div
              className="pictogram"
              style={{
                backgroundImage: `url("${ENDPOINTS.DOTNET}/rest/support/getImage?imageKey=${watchImageKey}")`,
              }}
            />
          )}
        </Field>

        <Field
          label="Problem Statement"
          required
          validationState={errors.problemStatement ? 'error' : 'none'}
          validationMessage={errors.problemStatement?.type === 'maxLength' ? 'Max length exceeded' : undefined}
        >
          <Textarea {...register('problemStatement', { maxLength: 512 })} rows={4} />
        </Field>

        <Field
          label="Key Questions"
          required
          validationState={errors.keyQuestions ? 'error' : 'none'}
          validationMessage={errors.keyQuestions?.type === 'maxLength' ? 'Max length exceeded' : undefined}
        >
          <Textarea {...register('keyQuestions', { maxLength: 512 })} rows={4} />
        </Field>

        <Field
          label="Solution Description"
          required
          validationState={errors.solutionDescription ? 'error' : 'none'}
          validationMessage={errors.solutionDescription?.type === 'maxLength' ? 'Max length exceeded' : undefined}
        >
          <Textarea {...register('solutionDescription', { maxLength: 8000 })} rows={6} />
        </Field>

        <Field
          label="Differentiated Benefit 1"
          validationState={errors.differentiatedBenefit1 ? 'error' : 'none'}
          validationMessage={errors.differentiatedBenefit1?.message}
        >
          <Input {...register('differentiatedBenefit1', {})} />
        </Field>

        <Field
          label="Benefit 1 Pictogram"
          validationState={errors.differentiatedBenefit1Pictogram ? 'error' : 'none'}
          validationMessage={errors.differentiatedBenefit1Pictogram?.message}
        >
          <Select
            //placeholder="select a picture"
            {...register('differentiatedBenefit1Pictogram', {
              maxLength: 256,
            })}
          >
            <option key={'none'} value={undefined}>
              None
            </option>
            {props.pictograms.map((p) => {
              return (
                <option key={p.imageKey} value={p.imageKey}>
                  {p.imageKey}
                </option>
              )
            })}
          </Select>
          {watchPict1 !== null && (
            <div
              className="pictogram"
              style={{
                backgroundImage: `url("${ENDPOINTS.DOTNET}/rest/support/getImage?imageKey=${watchPict1}")`,
              }}
            />
          )}
        </Field>

        <Field
          label="Differentiated Benefit 2"
          validationState={errors.differentiatedBenefit2 ? 'error' : 'none'}
          validationMessage={errors.differentiatedBenefit2?.message}
        >
          <Input {...register('differentiatedBenefit2', {})} />
        </Field>

        <Field
          label="Benefit 2 Pictogram"
          validationState={errors.differentiatedBenefit2Pictogram ? 'error' : 'none'}
          validationMessage={errors.differentiatedBenefit2Pictogram?.message}
        >
          <Select
            //placeholder="select a picture"
            {...register('differentiatedBenefit2Pictogram', {
              maxLength: 256,
            })}
          >
            <option key={''}>None</option>
            {props.pictograms.map((p) => {
              return (
                <option key={p.imageKey} value={p.imageKey}>
                  {p.imageKey}
                </option>
              )
            })}
          </Select>
          {watchPict2 !== null && (
            <div
              className="pictogram"
              style={{
                backgroundImage: `url("${ENDPOINTS.DOTNET}/rest/support/getImage?imageKey=${watchPict2}")`,
              }}
            />
          )}
        </Field>

        <Field
          label="Differentiated Benefit 3"
          validationState={errors.differentiatedBenefit3 ? 'error' : 'none'}
          validationMessage={errors.differentiatedBenefit3?.message}
        >
          <Input {...register('differentiatedBenefit3', {})} />
        </Field>

        <Field
          label="Benefit 3 Pictogram"
          validationState={errors.differentiatedBenefit3Pictogram ? 'error' : 'none'}
          validationMessage={errors.differentiatedBenefit3Pictogram?.message}
        >
          <Select
            //placeholder="select a picture"
            {...register('differentiatedBenefit3Pictogram', {
              maxLength: 256,
            })}
          >
            <option key={''}>None</option>
            {props.pictograms.map((p) => {
              return (
                <option key={p.imageKey} value={p.imageKey}>
                  {p.imageKey}
                </option>
              )
            })}
          </Select>
          {watchPict3 !== null && (
            <div
              className="pictogram"
              style={{
                backgroundImage: `url("${ENDPOINTS.DOTNET}/rest/support/getImage?imageKey=${watchPict3}")`,
              }}
            />
          )}
        </Field>

        <Field
          label="Key Contact User 1"
          validationState={errors.keyContactId1 ? 'error' : 'none'}
          validationMessage={errors.keyContactId1?.message}
        >
          <Select
            //placeholder="Key Contact User 1"
            {...register('keyContactId1', {})}
          >
            <option key={'none'} value={''}>
              None
            </option>
            {props.users.map((p) => {
              return (
                <option key={p.id} value={p.id}>
                  {p.givenName} {p.familyName}
                </option>
              )
            })}
          </Select>
        </Field>

        <Field
          label="Key Contact User 2"
          validationState={errors.keyContactId2 ? 'error' : 'none'}
          validationMessage={errors.keyContactId2?.message}
        >
          <Select
            //placeholder="Key Contact User 2"
            {...register('keyContactId2', {})}
          >
            <option key={'none'} value={''}>
              None
            </option>
            {props.users.map((p) => {
              return (
                <option key={p.id} value={p.id}>
                  {p.givenName} {p.familyName}
                </option>
              )
            })}
          </Select>
        </Field>

        <Field
          label="Key Contact User 3"
          validationState={errors.keyContactId3 ? 'error' : 'none'}
          validationMessage={errors.keyContactId3?.message}
        >
          <Select
            //placeholder="Key Contact User 3"
            {...register('keyContactId3', {})}
          >
            <option key={'none'} value={''}>
              None
            </option>
            {props.users.map((p) => {
              return (
                <option key={p.id} value={p.id}>
                  {p.givenName} {p.familyName}
                </option>
              )
            })}
          </Select>
        </Field>
      </div>
      <div className="form-action-container">
        <Button text="Submit" onClick={handleSubmit(onSubmit)} />
      </div>
    </div>
  )
}
